import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { MailToInput } from "@/lib/Components/Form/Inputs/MailToInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { mailBookingToSupplierMutation } from "@/app/Bookings/GraphQL/bookingMutations";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { toast } from "sonner";
import { useUser } from "@/lib/Auth/useUser";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import { Link } from "@/components/catalyst/link";
import { bookingRecordQuery } from "@/app/Bookings/GraphQL/bookingRecordQuery";
import { SelectInput } from "@/lib/Components/Form/Inputs/SelectInput";
import { BookingStatus, SupplierNotificationSettingEvent } from "@/gql/graphql";
import { supplierRecordQuery } from "@/app/Suppliers/GraphQL/supplierRecordQuery";

// Suppliers only ever receive the pending or the cancelled email. The backend
// picks the before/after confirmation variant of the cancelled email.
const SUPPLIER_EMAIL_OPTIONS = [
  {
    value: SupplierNotificationSettingEvent.BookingPending,
    label: "Booking Pending",
  },
  {
    value: SupplierNotificationSettingEvent.BookingCancelledBeforeConfirmation,
    label: "Booking Cancelled",
  },
];

const CANCELLED_STATUSES = [
  BookingStatus.AdminCancelled,
  BookingStatus.CustomerCancelled,
  BookingStatus.CustomerCancelledConfirmationTimeliness,
  BookingStatus.CustomerCancelledNoShow,
  BookingStatus.CustomerCancelledRebooked,
  BookingStatus.SupplierCancelled,
  BookingStatus.SupplierCancelledMechanicalFailure,
  BookingStatus.SupplierCancelledNotAvailable,
  BookingStatus.SupplierCancelledNoFerry,
];

function getDefaultEmailEvent(
  status: BookingStatus,
): SupplierNotificationSettingEvent {
  return CANCELLED_STATUSES.includes(status)
    ? SupplierNotificationSettingEvent.BookingCancelledBeforeConfirmation
    : SupplierNotificationSettingEvent.BookingPending;
}

function getEmailsForEvent(
  notificationSettings: {
    email: string;
    event: SupplierNotificationSettingEvent;
  }[],
  event: SupplierNotificationSettingEvent,
): string[] {
  return notificationSettings
    .filter((setting) => setting.event === event)
    .map((setting) => setting.email);
}

export function BookingMailToDialog({ bookingId }: { bookingId: string }) {
  const { data } = useSuspenseGqlQuery(bookingRecordQuery, {
    id: bookingId,
  });
  const { data: supplierData } = useSuspenseGqlQuery(supplierRecordQuery, {
    id: data.booking.supplier.id,
  });
  const { mutateAsync } = useGqlMutation(mailBookingToSupplierMutation);
  const { close } = useDialogState();
  const user = useUser();

  const booking = data.booking;
  const notificationSettings = supplierData.supplier.notificationSettings;
  const defaultEvent = getDefaultEmailEvent(booking.status);
  const supplierEmails = getEmailsForEvent(
    notificationSettings ?? [],
    defaultEvent,
  );
  const defaultEmails =
    supplierEmails.length > 0 ? supplierEmails : [user?.email].filter(Boolean);

  return (
    <BaseDialog
      title="Send Supplier Email"
      subtitle="Send details of this booking to the designated email addresses. This information is intended for the supplier"
    >
      <GenericForm<{
        emails: string[];
        event: SupplierNotificationSettingEvent;
      }>
        initialValues={{
          emails: defaultEmails,
          event: defaultEvent,
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: bookingId,
            emails: values.emails,
            event: values.event,
          });

          toast("Email sent", {
            description: `The email has been sent to ${values.emails.join(", ")}`,
          });

          return close();
        }}
      >
        <SelectInput
          name="event"
          label="Email Type"
          className="col-span-full"
          options={[
            {
              label: "Supplier Emails",
              options: SUPPLIER_EMAIL_OPTIONS,
            },
          ]}
        />

        <MailToInput
          name="emails"
          label="To"
          className="col-span-full"
          autoFocus
        />

        <div className="col-span-full flex items-center justify-end text-sm text-gray-500">
          <span>Not receiving emails?</span>

          <Link
            className="ml-2 underline"
            from="/$tenant/$orgId/supplier/{-$supplierId}"
            to="/$tenant/$orgId/supplier/{-$supplierId}/settings/notifications"
            params={(prev) => prev}
            search={{ supplier: data.booking.supplier.id }}
          >
            Edit notifications
            <ArrowTopRightOnSquareIcon className="ml-2 inline h-4 w-4" />
          </Link>
        </div>

        <DialogSubmitButton text="Send Email" />
      </GenericForm>
    </BaseDialog>
  );
}
